import { type DateTime } from "luxon";
import { useCallback, useRef, useState } from "react";

import { FIELD_NAME_DATE, FIELD_NAME_TIME_RANGE, type TextFieldValue } from "../../atoms/Form";
import { type FormValues, type MeetingTimeSlot } from "./AddEventTimesModal.types";
import {
  generateFieldName,
  getNextSelectedDay,
  getUserTimezone,
  isMeetingTimeComplete,
} from "./AddEventTimesModal.utils";

export const useMeetingTimeSlots = () => {
  const [meetingTimeSlots, setMeetingTimeSlots] = useState<MeetingTimeSlot[]>([{ displayIndex: 1, id: "0" }]);
  const [nextId, setNextId] = useState(1);

  const addMeetingTimeSlot = useCallback(() => {
    const newId = String(nextId);
    setMeetingTimeSlots((prev) => [...prev, { displayIndex: prev.length + 1, id: newId }]);
    setNextId((prev) => prev + 1);
    return newId;
  }, [nextId]);

  const removeMeetingTimeSlot = useCallback((idToRemove: string) => {
    setMeetingTimeSlots((prev) => {
      const filtered = prev.filter((slot) => slot.id !== idToRemove);
      // Reindex display numbers
      return filtered.map((slot, index) => ({ ...slot, displayIndex: index + 1 }));
    });
  }, []);

  const addMultipleMeetingTimeSlots = useCallback(
    (count: number) => {
      const newSlots: MeetingTimeSlot[] = [];
      let currentId = nextId;

      for (let i = 0; i < count; i++) {
        newSlots.push({
          displayIndex: meetingTimeSlots.length + i + 1,
          id: String(currentId),
        });
        currentId++;
      }

      setMeetingTimeSlots((prev) => [...prev, ...newSlots]);
      setNextId(currentId);
      return newSlots;
    },
    [nextId, meetingTimeSlots.length],
  );

  const resetSlots = useCallback(() => {
    setMeetingTimeSlots([{ displayIndex: 1, id: "0" }]);
    setNextId(1);
  }, []);

  return {
    addMeetingTimeSlot,
    addMultipleMeetingTimeSlots,
    meetingTimeSlots,
    removeMeetingTimeSlot,
    resetSlots,
  };
};

export const useSlotExpansion = () => {
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set(["0"]));
  const [autoCollapsedSlots, setAutoCollapsedSlots] = useState<Set<string>>(new Set());
  const [activeFieldSlots, setActiveFieldSlots] = useState<Set<string>>(new Set());
  const autoCollapseTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const toggleSlotExpansion = useCallback((slotId: string, onExpand?: (expandedId: string) => void) => {
    setExpandedSlots((prev) => {
      const newSet = new Set(prev);
      const isExpanding = !newSet.has(slotId);
      if (isExpanding) {
        newSet.add(slotId);
        // Wait for the accordion animation to complete before scrolling
        if (onExpand) {
          setTimeout(() => onExpand(slotId), 300); // MUI's default transition duration is 300ms
        }
      } else {
        newSet.delete(slotId);
      }
      return newSet;
    });
  }, []);

  const expandSlot = useCallback((slotId: string) => {
    setExpandedSlots((prev) => new Set(prev).add(slotId));
  }, []);

  const collapseSlot = useCallback((slotId: string) => {
    setExpandedSlots((prev) => {
      const newSet = new Set(prev);
      newSet.delete(slotId);
      return newSet;
    });
  }, []);

  const autoCollapseSlot = useCallback(
    (slotId: string) => {
      collapseSlot(slotId);
      setAutoCollapsedSlots((prev) => new Set(prev).add(slotId));
    },
    [collapseSlot],
  );

  const handleFieldFocus = useCallback((slotId: string) => {
    setActiveFieldSlots((prev) => new Set(prev).add(slotId));

    // Clear any pending auto-collapse for this slot
    const existingTimeout = autoCollapseTimeoutRef.current.get(slotId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      autoCollapseTimeoutRef.current.delete(slotId);
    }
  }, []);

  const handleFieldBlur = useCallback(
    (slotId: string, formValues: FormValues) => {
      setActiveFieldSlots((prev) => {
        const newSet = new Set(prev);
        newSet.delete(slotId);
        return newSet;
      });

      // Schedule auto-collapse check after user stops interacting
      const existingTimeout = autoCollapseTimeoutRef.current.get(slotId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      const timeoutId = setTimeout(() => {
        // Check if slot is complete and user is not actively interacting
        if (
          isMeetingTimeComplete(slotId, formValues) &&
          !autoCollapsedSlots.has(slotId) &&
          expandedSlots.has(slotId) &&
          !activeFieldSlots.has(slotId)
        ) {
          autoCollapseSlot(slotId);
        }
        autoCollapseTimeoutRef.current.delete(slotId);
      }, 1000); // 1 second delay after user stops interacting

      autoCollapseTimeoutRef.current.set(slotId, timeoutId);
    },
    [autoCollapsedSlots, expandedSlots, activeFieldSlots, autoCollapseSlot],
  );

  const resetExpansion = useCallback(() => {
    setExpandedSlots(new Set(["0"]));
    setAutoCollapsedSlots(new Set());
    setActiveFieldSlots(new Set());

    // Clear any pending auto-collapse timeouts
    autoCollapseTimeoutRef.current.forEach(clearTimeout);
    autoCollapseTimeoutRef.current.clear();
  }, []);

  const cleanupSlotExpansion = useCallback((slotId: string) => {
    setExpandedSlots((prev) => {
      const newSet = new Set(prev);
      newSet.delete(slotId);
      return newSet;
    });
    setAutoCollapsedSlots((prev) => {
      const newSet = new Set(prev);
      newSet.delete(slotId);
      return newSet;
    });
  }, []);

  return {
    activeFieldSlots,
    autoCollapseSlot,
    autoCollapsedSlots,
    cleanupSlotExpansion,
    collapseSlot,
    expandSlot,
    expandedSlots,
    handleFieldBlur,
    handleFieldFocus,
    resetExpansion,
    toggleSlotExpansion,
  };
};

export const useAutoAddMode = () => {
  const [autoAddMode, setAutoAddMode] = useState(false);
  const [autoAddEndDate, setAutoAddEndDate] = useState<DateTime | null>(null);

  const startAutoAddMode = useCallback(() => {
    setAutoAddMode(true);
  }, []);

  const cancelAutoAddMode = useCallback(() => {
    setAutoAddMode(false);
    setAutoAddEndDate(null);
  }, []);

  const setEndDate = useCallback((date: DateTime | null) => {
    setAutoAddEndDate(date);
  }, []);

  const resetAutoAddMode = useCallback(() => {
    setAutoAddMode(false);
    setAutoAddEndDate(null);
  }, []);

  return {
    autoAddEndDate,
    autoAddMode,
    cancelAutoAddMode,
    resetAutoAddMode,
    setEndDate,
    startAutoAddMode,
  };
};

export const useMeetingTimeOperations = (
  meetingTimeSlots: MeetingTimeSlot[],
  selectedDays: Set<number>,
  { addMeetingTimeSlot, addMultipleMeetingTimeSlots }: ReturnType<typeof useMeetingTimeSlots>,
  { autoCollapseSlot, expandSlot }: ReturnType<typeof useSlotExpansion>,
  scrollToElement: (slotId: string) => void,
) => {
  const addMeetingTime = useCallback(
    (
      formValues: FormValues,
      formApi: {
        change: (field: string, value: DateTime | [DateTime | null, DateTime | null] | TextFieldValue | null) => void;
      },
    ) => {
      const newId = addMeetingTimeSlot();

      // Copy values from the last meeting time if it exists
      if (meetingTimeSlots.length > 0) {
        const lastSlot = meetingTimeSlots[meetingTimeSlots.length - 1];
        const lastDate = formValues[generateFieldName(lastSlot.id, FIELD_NAME_DATE)] as DateTime | null;
        const lastTimeRange = formValues[generateFieldName(lastSlot.id, FIELD_NAME_TIME_RANGE)] as [
          DateTime | null,
          DateTime | null,
        ];
        const lastCapacity = formValues[generateFieldName(lastSlot.id, "capacity")] as TextFieldValue;

        // Auto-collapse the previous meeting time if it's complete and not already collapsed
        if (isMeetingTimeComplete(lastSlot.id, formValues)) {
          autoCollapseSlot(lastSlot.id);
        }

        // Set values for the new meeting time after a brief delay to ensure the fields exist
        setTimeout(() => {
          // Copy date and increment to next selected day, using user's timezone
          if (lastDate) {
            const userTimezone = getUserTimezone();
            const localLastDate = lastDate.setZone(userTimezone);
            const newDate = getNextSelectedDay(localLastDate, selectedDays);
            formApi.change(generateFieldName(newId, FIELD_NAME_DATE), newDate);
          }

          // Copy time range exactly, using user's timezone
          if (lastTimeRange && lastTimeRange[0] && lastTimeRange[1]) {
            const userTimezone = getUserTimezone();
            const localStartTime = lastTimeRange[0].setZone(userTimezone);
            const localEndTime = lastTimeRange[1].setZone(userTimezone);
            formApi.change(generateFieldName(newId, FIELD_NAME_TIME_RANGE), [localStartTime, localEndTime]);
          }

          // Copy capacity
          if (lastCapacity !== undefined) {
            formApi.change(generateFieldName(newId, "capacity"), lastCapacity);
          }

          // Copy series ID
          const lastSeriesId = formValues[generateFieldName(lastSlot.id, "seriesId")] as string | null;
          if (lastSeriesId !== undefined) {
            formApi.change(generateFieldName(newId, "seriesId"), lastSeriesId);
          }

          // Scroll to bottom to show the new meeting time
          scrollToElement(newId);
        }, 50);
      } else {
        // No previous meeting time, just add a new blank one
        // Expand empty forms by default so user can fill them out
        expandSlot(newId);

        // Scroll to bottom to show the new meeting time
        setTimeout(() => scrollToElement(newId), 50);
      }

      return newId;
    },
    [meetingTimeSlots, selectedDays, addMeetingTimeSlot, autoCollapseSlot, expandSlot, scrollToElement],
  );

  const autoAddMeetingTimes = useCallback(
    (
      formValues: FormValues,
      formApi: {
        change: (field: string, value: DateTime | [DateTime | null, DateTime | null] | TextFieldValue | null) => void;
      },
      endDate: DateTime,
    ) => {
      if (selectedDays.size === 0 || meetingTimeSlots.length === 0) {
        return;
      }

      const lastSlot = meetingTimeSlots[meetingTimeSlots.length - 1];
      const lastDate = formValues[generateFieldName(lastSlot.id, FIELD_NAME_DATE)] as DateTime | null;
      const lastTimeRange = formValues[generateFieldName(lastSlot.id, FIELD_NAME_TIME_RANGE)] as [
        DateTime | null,
        DateTime | null,
      ];
      const lastCapacity = formValues[generateFieldName(lastSlot.id, "capacity")] as TextFieldValue;

      if (!lastDate || !lastTimeRange || lastCapacity === undefined) {
        return;
      }

      const meetingTimesToAdd: { date: DateTime; timeRange: [DateTime, DateTime]; capacity: TextFieldValue }[] = [];
      let currentDate = getNextSelectedDay(lastDate, selectedDays);

      // Generate event times up to and including the end date
      while (currentDate <= endDate) {
        meetingTimesToAdd.push({
          capacity: lastCapacity,
          date: currentDate,
          timeRange: lastTimeRange as [DateTime, DateTime],
        });
        currentDate = getNextSelectedDay(currentDate, selectedDays);
      }

      // Auto-collapse the current last meeting time if complete
      if (isMeetingTimeComplete(lastSlot.id, formValues)) {
        autoCollapseSlot(lastSlot.id);
      }

      // Add all the new meeting time slots
      const newSlots = addMultipleMeetingTimeSlots(meetingTimesToAdd.length);

      // Set values for all new meeting times with DST-aware time handling
      setTimeout(() => {
        meetingTimesToAdd.forEach((meetingTime, index) => {
          const slotId = newSlots[index].id;

          // Preserve local time by creating new time objects on the target date in user's timezone
          const userTimezone = getUserTimezone();
          const localStartTime = lastTimeRange[0]?.setZone(userTimezone);
          const localEndTime = lastTimeRange[1]?.setZone(userTimezone);

          if (localStartTime && localEndTime) {
            const originalStartHour = localStartTime.hour;
            const originalStartMinute = localStartTime.minute;
            const originalEndHour = localEndTime.hour;
            const originalEndMinute = localEndTime.minute;

            // Ensure the meeting date is in user's timezone
            const localMeetingDate = meetingTime.date.setZone(userTimezone);

            // Create new time range with the same local times on the new date in user's timezone
            const newStartTime = localMeetingDate.set({
              hour: originalStartHour,
              millisecond: 0,
              minute: originalStartMinute,
              second: 0,
            });

            const newEndTime = localMeetingDate.set({
              hour: originalEndHour,
              millisecond: 0,
              minute: originalEndMinute,
              second: 0,
            });

            formApi.change(generateFieldName(slotId, FIELD_NAME_DATE), localMeetingDate);
            formApi.change(generateFieldName(slotId, FIELD_NAME_TIME_RANGE), [newStartTime, newEndTime]);
            formApi.change(generateFieldName(slotId, "capacity"), meetingTime.capacity);

            // Copy series ID
            const lastSeriesId = formValues[generateFieldName(lastSlot.id, "seriesId")] as string | null;
            if (lastSeriesId !== undefined) {
              formApi.change(generateFieldName(slotId, "seriesId"), lastSeriesId);
            }
          }
        });

        // Scroll to bottom to show the new meeting times
        scrollToElement(newSlots[newSlots.length - 1].id);
      }, 50);
    },
    [selectedDays, meetingTimeSlots, addMultipleMeetingTimeSlots, autoCollapseSlot, scrollToElement],
  );

  return {
    addMeetingTime,
    autoAddMeetingTimes,
  };
};
